import { Injectable, inject } from "@angular/core";
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from "@angular/common/http";

import { BehaviorSubject, Observable, of } from "rxjs";
import { switchMap, catchError, filter, take, tap } from "rxjs/operators";
import { CognitoUserSession } from "amazon-cognito-identity-js";

import { IcEnvironment } from "@intocare-medapp/environment";

import { Config } from "../entities";
import { AWSAuthService } from "../services/aws-auth.service";
import { COGNITO_SERVICE_CONFIGURATION } from "../tokens";
import { AppSessionService, LocalStorageService, DeviceMetricsService, LanguageService } from "app/shared";
import { getApplicationName } from "app/shared/utils";

interface ApiCallWithoutAuth {
	endpoint: string;
	method: string;
}

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
	private readonly config = inject<Config>(COGNITO_SERVICE_CONFIGURATION);

	private readonly authService = inject(AWSAuthService);
	private readonly localStorageService = inject(LocalStorageService);
	private readonly icEnvironment = inject(IcEnvironment);
	private readonly appSessionService = inject(AppSessionService);
	private readonly languageService = inject(LanguageService);

	private environment = this.icEnvironment.getEnvironment();
	private refreshing = false;

	private readonly paused$ = new BehaviorSubject(false);

	private readonly endpointsWithoutAuth: ApiCallWithoutAuth[] = [
		{
			endpoint: "/userpool",
			method: "GET",
		},
	];

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (!this.isApiGatewayUrl(request.url)) {
			return next.handle(request);
		}

		const isEndpointWithoutAuth = this.endpointsWithoutAuth.some(
			({ endpoint, method }) => request.method.toUpperCase() === method.toUpperCase() && request.url.includes(endpoint),
		);

		if (isEndpointWithoutAuth) {
			return this.invoke(request, next);
		}

		return this.paused$.pipe(
			// If the stream is paused, drop it. It will re-evaluate whenever `paused$` changes it's value
			filter((isPaused) => !isPaused),
			// Retrieve the user session
			switchMap(() => this.authService.getUserSession()),
			// If we are currently refreshing, drop it. It will re-evaluate whenever the source stream is unpaused.
			filter(() => !this.refreshing),
			// Automatically unsubscribe after it passes the filters.
			take(1),
			switchMap((session: CognitoUserSession) => {
				if (session && this.isExpired(session.getAccessToken().getExpiration())) {
					// This.refreshing = true;
					// This.paused$.next(true);

					return this.authService.refresh().pipe(
						tap(() => {
							this.refreshing = false;
							this.paused$.next(false);
						}),
						catchError((error) => {
							this.authService.onRefreshErrorHandler(error);

							this.refreshing = false;
							this.paused$.next(false);

							return of(session);
						}),
					);
				}

				return of(session);
			}),
			switchMap((session) => this.invoke(request, next, session)),
		);
	}

	private invoke(
		request: HttpRequest<any>,
		next: HttpHandler,
		cognitoUserSession?: CognitoUserSession,
	): Observable<any> {
		const headers: any = {};

		const applicationName = getApplicationName();

		headers["x-medapp-version"] = this.environment.APP_VERSION;
		headers["x-application"] = `${applicationName}/${this.environment.APP_VERSION}`;

		if (this.config.apiKey) {
			// Set API key if defined
			headers["x-api-key"] = this.config.apiKey;
		}

		const appInstance = this.localStorageService.getAppInstance();

		// Id that will stay the same for app installation
		headers["X-App-Instance-Id"] = appInstance;

		// This will not be present if the user has not logged in yet
		if (this.localStorageService.getUserSessionId()) {
			// Id that will stay the same for user login session
			headers["X-User-Session"] = this.localStorageService.getUserSessionId();
		}

		// Id that will stay the same for app runtime session
		headers["X-App-Session"] = this.appSessionService.appSessionId;
		// Timestamp when the app was started
		headers["X-App-Start-Time"] = this.localStorageService.getAppStartTimeUnix();

		headers["X-IC-Device-Info"] = DeviceMetricsService.getIcDeviceInfoHeaderValueNow();

		if (cognitoUserSession && request.method !== "HEAD") {
			// Set JWT token of we have a user session
			headers.Authorization = `Bearer ${cognitoUserSession.getAccessToken().getJwtToken()}`;
		}

		headers["Accept-Language"] = this.languageService.getLocale();

		const modifiedRequest = request.clone({
			setHeaders: headers,
		});

		return next.handle(modifiedRequest);
	}

	private isApiGatewayUrl(requestUrl: string) {
		const pattern = this.config.urlPattern || /^https:\/\/(?:.*?)\.into.care/;

		return pattern.test(requestUrl);
	}

	private isExpired(exp: number, offset = 300000) {
		return Date.now() > exp * 1000 - offset;
	}
}
